import { useState } from "react";
import { useForm } from "react-hook-form";
import type { Profile, Permission } from "@core/types";
import type { PermissionModule } from "@core/session/types";
import {
  Field,
  FormLayout,
  PortalButton,
  Toggle,
  inputStyle,
  useApiFieldErrors,
  useConfirmUnsavedChanges,
} from "@portal/common";
import { tokens } from "@core/theme/tokens";
import {
  PROFILE_FORM_FIELDS,
  profileFormSchema,
  type ProfileFormValues,
} from "./profileSchemas";
import {
  ADMINISTRATOR_PROFILE_ID,
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  PORTAL_MODULES,
  clearPortalModules,
  emptyMatrix,
  matrixFromProfile,
  toggleMatrixAction,
  withAdministratorSecurityLocked,
} from "./permissionMatrix";

export type ProfileDraft = {
  name: string;
  description?: string;
  validatorOnly: boolean;
  permissions: Permission[];
};

function PermissionCheckbox({
  checked,
  disabled,
  onToggle,
  ariaLabel,
}: {
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onToggle}
      style={{
        width: 22,
        height: 22,
        borderRadius: 7,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.55 : 1,
        background: checked
          ? `linear-gradient(135deg,${tokens.gold.light},${tokens.gold.deep})`
          : "rgba(255,255,255,0.04)",
        border: `1px solid ${checked ? tokens.gold.line : tokens.surface.line2}`,
      }}
    >
      {checked ? (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            background: "#16201B",
          }}
        />
      ) : null}
    </button>
  );
}

// SPEC-216 §4/§4.1/§5: matriz de siete módulos × cuatro acciones, con dos
// bloqueos de interfaz superpuestos (el backend reaplica ambos igual —
// esto es solo feedback inmediato, web/AGENTS.md):
// - `validatorOnly`: bloquea los seis módulos del portal (RF-SEG-14).
// - Administrador (por id fijo, ver permissionMatrix.ts): bloquea Seguridad
//   sola, siempre en "todo marcado" (RF-SEG-13/§5).
function PermissionMatrixEditor({
  matrix,
  onChange,
  validatorOnly,
  lockSecurityModule,
}: {
  matrix: Permission[];
  onChange: (next: Permission[]) => void;
  validatorOnly: boolean;
  lockSecurityModule: boolean;
}) {
  const toggle = (
    module: PermissionModule,
    action: (typeof PERMISSION_ACTIONS)[number]["action"],
  ) => {
    onChange(toggleMatrixAction(matrix, module, action));
  };
  return (
    <div
      style={{
        border: `1px solid ${tokens.surface.line}`,
        borderRadius: tokens.radius.lg,
        overflow: "hidden",
        opacity: validatorOnly ? 0.45 : 1,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr repeat(4, 92px)",
          gap: 8,
          alignItems: "center",
          padding: "14px 18px 10px",
          borderBottom: `1px solid ${tokens.surface.line}`,
        }}
      >
        <span className="label">Módulo</span>
        {PERMISSION_ACTIONS.map(({ action, label }) => (
          <span key={action} className="label" style={{ textAlign: "center" }}>
            {label}
          </span>
        ))}
      </div>
      {PERMISSION_MODULES.map(({ module, label }) => {
        const permission = matrix.find((item) => item.module === module);
        // Bloqueada por comercio aliado (los seis módulos del portal) o,
        // solo para Administrador, el módulo Seguridad exclusivamente.
        const disabledRow =
          (validatorOnly && PORTAL_MODULES.includes(module)) ||
          (lockSecurityModule && module === "security");
        return (
          <div
            key={module}
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr repeat(4, 92px)",
              gap: 8,
              alignItems: "center",
              padding: "11px 18px",
              borderBottom: `1px solid ${tokens.surface.line}`,
            }}
          >
            <span
              style={{
                font: `600 12.5px ${tokens.font.ui}`,
                color: tokens.text.secondary,
              }}
            >
              {label}
            </span>
            {PERMISSION_ACTIONS.map(({ action, label: actionLabel }) => (
              <div
                key={action}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <PermissionCheckbox
                  checked={Boolean(permission?.[action])}
                  disabled={disabledRow}
                  ariaLabel={`${label} · ${actionLabel}`}
                  onToggle={() => toggle(module, action)}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function ProfileForm({
  profile,
  onSubmit,
  onCancel,
  submitting,
  modal,
  submitError,
  onUnhandledSubmitError,
}: {
  profile: Profile | undefined;
  onSubmit: (input: ProfileDraft) => void;
  onCancel: () => void;
  submitting: boolean;
  // El formulario ES el diálogo (revisión en vivo 2026-09-22 punto #4A):
  // título y acciones van a las regiones fijas del `Modal`, no al scroll.
  modal?: { label: string; width?: number | string; onClose?: () => void };
  // Mismo criterio que `CatalogEntryForm`/`MemberForm`: `PROFILE_DUPLICATE`
  // (`authorization.rules.ts`) solo puede detectarse en el servidor. El 409
  // vive en `ProfilesScreen` (dueño del `useToast`), se lo pasa acá para que
  // `applyApiFieldErrors` lo asocie a `name`; si no puede, se lo devuelve por
  // `onUnhandledSubmitError` para el toast de siempre.
  submitError?: unknown;
  onUnhandledSubmitError?: (error: unknown) => void;
}) {
  const isAdministrator = profile?.id === ADMINISTRATOR_PROFILE_ID;
  const [matrix, setMatrix] = useState<Permission[]>(() => {
    const base = profile
      ? matrixFromProfile(profile.permissions)
      : emptyMatrix();
    return isAdministrator ? withAdministratorSecurityLocked(base) : base;
  });
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<ProfileFormValues>({
    defaultValues: {
      name: profile?.name ?? "",
      description: profile?.description ?? "",
      validatorOnly: profile?.validatorOnly ?? false,
    },
  });
  useConfirmUnsavedChanges(isDirty);
  const validatorOnly = watch("validatorOnly");
  const name = watch("name");
  useApiFieldErrors(
    submitError,
    setError as (
      field: string,
      error: { type: string; message: string },
    ) => void,
    PROFILE_FORM_FIELDS,
    onUnhandledSubmitError,
  );

  const handleValidatorOnlyChange = (next: boolean) => {
    setValue("validatorOnly", next, { shouldDirty: true });
    if (next) setMatrix((current) => clearPortalModules(current));
  };
  // PLAN-009 §3.6: cada issue de zod cae bajo SU campo — `return` mudo
  // descartaba el issue sin pintar nada (SPEC-012 criterio 12).
  const submit = (values: ProfileFormValues) => {
    const parsed = profileFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof ProfileFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    const finalMatrix = isAdministrator
      ? withAdministratorSecurityLocked(matrix)
      : parsed.data.validatorOnly
        ? clearPortalModules(matrix)
        : matrix;
    onSubmit({
      name: parsed.data.name,
      validatorOnly: parsed.data.validatorOnly,
      permissions: finalMatrix,
      ...(parsed.data.description
        ? { description: parsed.data.description }
        : {}),
    });
  };

  return (
    <FormLayout
      // Punto #21 (revisión en vivo 2026-09-22): como en "Nueva recompensa",
      // la acción arriba y el nombre que se está escribiendo como título.
      eyebrow={profile ? "Editar perfil" : "Nuevo perfil"}
      title={name || "Perfil sin nombre"}
      {...(modal ? { modal } : {})}
      onSubmit={handleSubmit(submit)}
      actions={
        <>
          <PortalButton
            type="button"
            tone="secondary"
            size="small"
            onClick={onCancel}
          >
            Cancelar
          </PortalButton>
          <PortalButton type="submit" size="small" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar perfil"}
          </PortalButton>
        </>
      }
    >
      <div>
        <Field label="Nombre del perfil" required error={errors.name?.message}>
          <input {...register("name")} style={inputStyle} />
        </Field>
      </div>
      <Field label="Descripción">
        <textarea
          {...register("description")}
          style={{ ...inputStyle, minHeight: 62, resize: "vertical" }}
        />
      </Field>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          borderRadius: 13,
          border: `1px solid ${tokens.surface.line2}`,
        }}
      >
        <Toggle
          on={validatorOnly}
          onChange={handleValidatorOnlyChange}
          ariaLabel="Perfil de comercio aliado"
        />
        <div>
          <div
            style={{
              font: `600 12.5px ${tokens.font.ui}`,
              color: tokens.text.primary,
            }}
          >
            Perfil de comercio aliado
          </div>
          <div
            style={{
              font: `400 11.5px ${tokens.font.ui}`,
              color: tokens.text.tertiary,
              marginTop: 2,
            }}
          >
            Acceso exclusivo al validador de QR, sin ingreso al portal
            administrativo.
          </div>
        </div>
      </div>

      {validatorOnly ? (
        <div
          role="alert"
          style={{
            display: "flex",
            gap: 11,
            padding: "13px 15px",
            borderRadius: 13,
            background: tokens.gold.dim,
            border: `1px solid ${tokens.gold.line}`,
            font: `400 12px ${tokens.font.ui}`,
            color: tokens.text.secondary,
          }}
        >
          Ningún usuario con este perfil podrá ingresar al portal
          administrativo: solo puede iniciar sesión en el validador de QR.
        </div>
      ) : null}

      {isAdministrator ? (
        <div
          role="note"
          style={{
            font: `400 11.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          El perfil Administrador conserva siempre acceso total a Seguridad; ese
          módulo queda fijo en la matriz.
        </div>
      ) : null}

      <div className="label" style={{ marginBottom: 10 }}>
        Permisos por módulo
      </div>
      <PermissionMatrixEditor
        matrix={matrix}
        onChange={setMatrix}
        validatorOnly={validatorOnly}
        lockSecurityModule={isAdministrator}
      />
    </FormLayout>
  );
}
